import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Link
} from '@mui/material';
import {
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthService } from '../../services/AuthService';

interface EmailVerificationProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

const EmailVerification: React.FC<EmailVerificationProps> = ({
  onSuccess,
  onCancel
}) => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [token, setToken] = useState(searchParams.get('token') || '');
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  
  const navigate = useNavigate();

  // Verify email on component mount if token is provided
  useEffect(() => {
    if (email && token) {
      verifyEmail();
    }
  }, [email, token]);

  const verifyEmail = async () => {
    setVerifying(true);
    setError('');
    
    try {
      const response = await AuthService.verifyEmail({ email, token });
      
      if (response.success) {
        setSuccess(true);
        if (onSuccess) {
          onSuccess();
        } else {
          // Redirect to login after 3 seconds
          setTimeout(() => navigate('/login'), 3000);
        }
      } else {
        setError(response.message || 'Email verification failed');
      }
    } catch (err: any) {
      setError(err.message || 'Invalid or expired verification token');
    } finally {
      setVerifying(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      setError('Email address is required');
      return;
    }

    setResending(true);
    setError('');
    setResendSuccess(false);

    try {
      const response = await AuthService.resendVerification({ email });
      
      if (response.success) {
        setResendSuccess(true);
      } else {
        setError(response.message || 'Failed to resend verification email');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to resend verification email');
    } finally {
      setResending(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate('/login');
    }
  };

  // Show loading while verifying
  if (verifying) {
    return (
      <Paper sx={{ p: 4, maxWidth: 500, mx: 'auto', mt: 4 }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography variant="h6">Verifying your email...</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Please wait while we verify your email address.
          </Typography>
        </Box>
      </Paper>
    );
  }

  // Show success message
  if (success) {
    return (
      <Paper sx={{ p: 4, maxWidth: 500, mx: 'auto', mt: 4 }}>
        <Box sx={{ textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom color="success.main">
            Email Verified Successfully
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Thank you! Your email address has been verified. You can now access all features of your account.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/login')}
            size="large"
          >
            Continue to Sign In
          </Button>
        </Box>
      </Paper>
    );
  }

  // Show error or resend interface
  return (
    <Paper sx={{ p: 4, maxWidth: 500, mx: 'auto', mt: 4 }}>
      <Box sx={{ textAlign: 'center' }}>
        {error ? (
          <ErrorIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
        ) : (
          <EmailIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
        )}
        
        <Typography variant="h5" gutterBottom>
          Email Verification
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
            {error}
          </Alert>
        )}

        {resendSuccess && (
          <Alert severity="success" sx={{ mb: 3, textAlign: 'left' }}>
            Verification email has been sent! Please check your inbox and follow the instructions.
          </Alert>
        )}

        {!token ? (
          <>
            <Typography variant="body1" sx={{ mb: 3 }}>
              We need to verify your email address to complete your account setup. 
              Please check your email for a verification link.
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              If you haven't received the email, check your spam folder or request a new verification email.
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Enter your email address to resend the verification email:
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'center' }}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  style={{
                    padding: '10px',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    fontSize: '14px',
                    width: '200px'
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleResendVerification}
                  disabled={resending || !email}
                  startIcon={resending ? <CircularProgress size={16} /> : <SendIcon />}
                >
                  {resending ? 'Sending...' : 'Resend'}
                </Button>
              </Box>
            </Box>
          </>
        ) : (
          <>
            <Typography variant="body1" sx={{ mb: 3 }}>
              There was an issue verifying your email address. The verification link may have expired or is invalid.
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 3 }}>
              <Button
                variant="outlined"
                onClick={() => verifyEmail()}
                disabled={loading}
              >
                Try Again
              </Button>
              
              <Button
                variant="contained"
                onClick={handleResendVerification}
                disabled={resending}
                startIcon={resending ? <CircularProgress size={16} /> : <SendIcon />}
              >
                {resending ? 'Sending...' : 'Resend Email'}
              </Button>
            </Box>
          </>
        )}

        <Box sx={{ mt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Need help?{' '}
            <Link 
              component="button" 
              variant="body2" 
              onClick={(e) => {
                e.preventDefault();
                // In a real app, this would go to support
                navigate('/contact');
              }}
            >
              Contact Support
            </Link>
            {' or '}
            <Link 
              component="button" 
              variant="body2" 
              onClick={(e) => {
                e.preventDefault();
                handleCancel();
              }}
            >
              Back to Sign In
            </Link>
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
};

export default EmailVerification;